import { Suspense, useCallback, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { RouteFallback } from "./RouteFallback";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

const SIDEBAR_STORAGE_KEY = "aether:sidebar-collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

interface AppShellProps {
  pageTitle?: string;
}

export function AppShell({ pageTitle = "" }: AppShellProps) {
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  // Mobile drawer (UX-14): independent of the desktop collapse preference.
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.documentElement.setAttribute("data-sidebar", collapsed ? "collapsed" : "expanded");
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      // ignore — local-only preference.
    }
  }, [collapsed]);

  // Close the mobile drawer on navigation.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const toggle = useCallback(() => setCollapsed((v) => !v), []);

  return (
    <div
      className="app"
      data-side={collapsed ? "collapsed" : "expanded"}
      data-mobile-open={mobileOpen ? "true" : "false"}
    >
      <Sidebar collapsed={collapsed} onToggleCollapsed={toggle} />
      {mobileOpen && (
        <div
          className="sidebar-backdrop"
          aria-hidden="true"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <div className="main">
        <Topbar pageTitle={pageTitle} onOpenMobileNav={() => setMobileOpen(true)} />
        <main className="content relative">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
